import { Component, Inject, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ensureMiniMenuThemeFonts,
  MINI_MENU_FONTS,
  miniMenuFontStack,
  MiniMenuTheme
} from 'src/app/services/mini-menu.service';

interface KeyTitleOption {
  id: string;
  title: string;
}

/** What the modal hands back — the theme fields it owns, or nothing if cancelled. */
export interface MiniMenuAppearanceResult {
  font: string;
  backgroundColor: string;
  uiColor: string;
  contentColor: string;
  accentColor: string;
}

/**
 * Appearance editor. The controls sit beside a miniature portrait of the MiniMenu landing
 * screen that re-renders as the operator changes them, so the choice is made against the thing
 * it affects rather than against four hex fields.
 *
 * The mock mirrors the real teaser's structure and colour roles (see mini-menu-site.component
 * .html #teaser): background = --gray, interface chrome = --blue, body copy = --ink,
 * accent = --gold.
 */
@Component({
  selector: 'app-mini-menu-appearance-modal',
  templateUrl: './mini-menu-appearance-modal.component.html',
  styleUrls: ['./mini-menu-appearance-modal.component.scss']
})
export class MiniMenuAppearanceModalComponent implements OnInit {
  // Set by the opener before the modal renders.
  theme!: MiniMenuTheme;
  /** Copy shown in the mock, so the preview reads like this business rather than lorem. */
  businessName = 'Your Business';
  locationTag = '';
  eyebrow = '';
  subline = '';

  readonly fonts: KeyTitleOption[] = MINI_MENU_FONTS;
  fontSelect: KeyTitleOption = MINI_MENU_FONTS[0];

  // Edited copy — the caller's theme is left untouched until Save.
  working = {
    font: 'hanken',
    backgroundColor: '#ECECEA',
    uiColor: '#001E46',
    contentColor: '#16242B',
    accentColor: '#887A57'
  };

  constructor(private activeModal: NgbActiveModal, @Inject(DOCUMENT) private document: Document) {}

  ngOnInit(): void {
    // Without the webfont link the preview silently falls back to system fonts.
    ensureMiniMenuThemeFonts(this.document);

    if (this.theme) {
      this.working = {
        font: this.theme.font || 'hanken',
        backgroundColor: this.theme.backgroundColor,
        uiColor: this.theme.uiColor,
        contentColor: this.theme.contentColor,
        accentColor: this.theme.accentColor
      };
    }
    this.fontSelect = this.fonts.find((f) => f.id === this.working.font) || this.fonts[0];
  }

  get fontStack(): string {
    return miniMenuFontStack(this.working.font);
  }

  get previewLocation(): string {
    return this.locationTag || this.businessName;
  }

  get previewEyebrow(): string {
    return this.eyebrow || 'Digital menu';
  }

  get previewSubline(): string {
    return this.subline || 'Browse the menu, see what is on, and order at the counter.';
  }

  onFontChange(option: KeyTitleOption): void {
    this.fontSelect = option || this.fonts[0];
    this.working.font = this.fontSelect.id;
  }

  save(): void {
    this.activeModal.close({ ...this.working } as MiniMenuAppearanceResult);
  }

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
